import { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, Button, CircularProgress, Alert, Container,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  cancelTeamEvent, deleteTeamEvent, cancelTeamMatchup, deleteTeamMatchup, applyRecurringAction,
} from '../../services/scheduleService';
import { EventFormDialog, CompleteGameDialog, RecurringApplyToRadios, PostponeEventDialog, ReopenGameDialog } from '../../components/teams/GameFormDialogs';
import DivisionHub from '../../components/programs/DivisionHub';
import TeamHubLayout from '../../components/teams/TeamHubLayout';
import { formatGameOpponentLine } from '../../utils/gameResult';
import { loadScheduleData } from './scheduleController';
import { resolveDivisionBackNav } from './scheduleNav';

// Phase C.5e.1 — SCHEDULE CONSOLIDATION. THE ONE canonical authenticated
// schedule page — TeamSchedulePage.js and DivisionSchedulePage.js are now
// thin route wrappers that resolve their own route params and render
// THIS component with the right props; neither contains any schedule
// data-loading or presentation logic of its own anymore. See
// scheduleController.js's own header for the full "why" and the browser
// bug this consolidation fixes (a plain Head Coach's "View Full
// Division" link used to land on a DIFFERENT, unfixed loader).
//
// `teamId` (TEAM-FOCUSED MODE) XOR `programId`+`divisionId` (DIVISION
// MODE) — exactly one of these shapes is passed by the two route
// wrappers below. Everything else (Program/Division/membership/
// authority/perTeam) is resolved by the ONE loadScheduleData() algorithm
// regardless of which mode this is.
//
// `fromTeamId` (DIVISION MODE only) — Phase C.5e.2: the originating
// Team when this Division view was reached via a Team-focused "View
// Full Division →" link (DivisionScheduleRoute reads it off the
// `?fromTeam=` query string — see resolveDivisionBackNav()'s own header
// for the full authority-aware back-navigation story this fixes).
export default function SchedulePage({ teamId, programId, divisionId, fromTeamId }) {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [team, setTeam] = useState(null);
  const [division, setDivision] = useState(null);
  const [perTeam, setPerTeam] = useState([]);
  const [canManage, setCanManage] = useState(false);
  const [canManageRow, setCanManageRow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [formOpen, setFormOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [completeTarget, setCompleteTarget] = useState(null);
  const [postponeTarget, setPostponeTarget] = useState(null);
  const [reopenTarget, setReopenTarget] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState(null);
  const [cancelApplyTo, setCancelApplyTo] = useState('this');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [deleteApplyTo, setDeleteApplyTo] = useState('this');

  const load = useCallback(async () => {
    const result = await loadScheduleData({ teamId, programId, divisionId, currentUserId: currentUser?.id });
    setTeam(result.team);
    setDivision(result.division);
    setPerTeam(result.perTeam);
    setCanManage(result.canManage);
    // canManageRow is a function — React's setState treats a function
    // value as an updater unless wrapped, hence the () => fn form.
    setCanManageRow(() => result.canManageRow);
  }, [teamId, programId, divisionId, currentUser?.id]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    load().catch(e => { if (!cancelled) setError(e.message); }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [load]);

  const handleAddClick = () => { setEditingRow(null); setFormOpen(true); };
  const handleEditClick = (row) => { setEditingRow(row); setFormOpen(true); };
  const handleCancelClick = (row) => { setCancelApplyTo('this'); setCancelTarget(row); };
  const handleDeleteClick = (row) => { setDeleteApplyTo('this'); setDeleteTarget(row); };

  const handleCancelConfirm = async () => {
    setCancelError(null);
    setCancelling(true);
    try {
      if (cancelTarget.series_id && cancelApplyTo !== 'this') {
        // Phase C.5b — "This and future" / "Entire series" Cancel (044).
        await applyRecurringAction({ eventId: cancelTarget.id, applyTo: cancelApplyTo, action: 'cancel' });
      } else if (cancelTarget.kind === 'matchup') {
        await cancelTeamMatchup(cancelTarget.id);
      } else {
        await cancelTeamEvent(cancelTarget.id);
      }
      await load();
      setCancelTarget(null);
    } catch (e) {
      setCancelError(e.message || 'Could not cancel this event.');
    } finally {
      setCancelling(false);
    }
  };

  const handleDeleteConfirm = async () => {
    setDeleteError(null);
    setDeleting(true);
    try {
      if (deleteTarget.series_id && deleteApplyTo !== 'this') {
        // Phase C.5b — "This and future" / "Entire series" Delete (044)
        // — tombstones (status='excluded') the matching occurrences;
        // never a hard delete for a recurring row.
        await applyRecurringAction({ eventId: deleteTarget.id, applyTo: deleteApplyTo, action: 'exclude' });
      } else if (deleteTarget.kind === 'matchup') {
        await deleteTeamMatchup(deleteTarget.id);
      } else {
        await deleteTeamEvent(deleteTarget.id);
      }
      await load();
      setDeleteTarget(null);
    } catch (e) {
      setDeleteError(e.message || 'Could not delete this event.');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) return <Box sx={{ py: 10, textAlign: 'center' }}><CircularProgress sx={{ color: '#2ea84b' }} /></Box>;
  if (error) return <Container maxWidth="sm" sx={{ py: 6 }}><Alert severity="error">{error}</Alert></Container>;
  if (teamId && !team) return null;
  // DIVISION MODE has no "own Team" fallback content the way Team-
  // focused mode does — Division metadata (getDivision(), inside
  // loadScheduleData()'s own best-effort try/catch) failing here means
  // there is genuinely nothing to show, so this surfaces as an explicit
  // error rather than a silent blank page (matching the original
  // DivisionSchedulePage.js's own error-surfacing behavior).
  if (!teamId && !division) return <Container maxWidth="sm" sx={{ py: 6 }}><Alert severity="error">This division could not be loaded.</Alert></Container>;

  const isTeamFocused = Boolean(teamId);

  // EventFormDialog's own "Applies To" (Entire Division/Specific Teams)
  // picker only ever appears in a multi-Team context (teamOptions.length
  // > 1) — Team-focused mode deliberately stays single-Team (never
  // auto-grants Entire-Division scope from a Team's own Schedule, exactly
  // as before this consolidation); Division mode offers the full roster,
  // exactly as before.
  const teamOptions = isTeamFocused
    ? (team ? [{ id: team.id, team_name: team.team_name }] : [])
    : perTeam.map(({ team: t }) => ({ id: t.id, team_name: t.team_name }));

  const divisionForHub = division || (team ? { name: team.team_name, competition_type: 'other', season_name: null } : null);
  // Phase C.5e.2 — carries this Team's own id through as `?fromTeam=`, so
  // Division mode can offer an authority-aware "back" destination (see
  // resolveDivisionBackNav()) instead of a hardcoded, Program-management-
  // only "Seasons & Divisions" link a plain Head Coach cannot use.
  const divisionHref = (isTeamFocused && team?.program_id && team?.division_id)
    ? `/my-programs/${team.program_id}/divisions/${team.division_id}/schedule?fromTeam=${team.id}`
    : null;
  const getTeamHref = isTeamFocused
    ? (t) => (t.id === team.id ? null : `/my-teams/${t.id}/schedule`)
    : (t) => `/my-teams/${t.id}/schedule`;

  const hub = (
    <>
      <DivisionHub
        division={divisionForHub}
        perTeam={perTeam}
        canManage={canManage}
        canManageRow={canManageRow}
        focusTeamId={isTeamFocused ? team.id : undefined}
        onAddEvent={handleAddClick}
        onEditRow={handleEditClick}
        onCompleteRow={setCompleteTarget}
        onPostponeRow={setPostponeTarget}
        onReopenRow={setReopenTarget}
        onCancelRow={handleCancelClick}
        onDeleteRow={handleDeleteClick}
        getTeamHref={getTeamHref}
      />

      <EventFormDialog
        open={formOpen} onClose={() => setFormOpen(false)} onSaved={load}
        teamId={isTeamFocused ? teamId : undefined}
        teamOptions={teamOptions} event={editingRow}
        divisionId={isTeamFocused ? undefined : divisionId}
      />

      <CompleteGameDialog
        open={Boolean(completeTarget)} onClose={() => setCompleteTarget(null)} onSaved={load} event={completeTarget}
      />

      <PostponeEventDialog
        open={Boolean(postponeTarget)} onClose={() => setPostponeTarget(null)} onSaved={load} event={postponeTarget}
        subjectLine={
          postponeTarget && (
            postponeTarget.event_type === 'game' || postponeTarget.kind === 'matchup'
              ? <>{postponeTarget.ownerTeamName} {formatGameOpponentLine(postponeTarget)}</>
              : (postponeTarget.title || 'This event')
          )
        }
      />

      <ReopenGameDialog
        open={Boolean(reopenTarget)} onClose={() => setReopenTarget(null)} onSaved={load} event={reopenTarget}
        subjectLine={reopenTarget && <>{reopenTarget.ownerTeamName} {formatGameOpponentLine(reopenTarget)}</>}
      />

      <Dialog
        open={Boolean(cancelTarget)}
        onClose={() => { if (!cancelling) setCancelTarget(null); }}
        PaperProps={{ sx: { backgroundColor: 'rgba(10,20,34,0.98)', border: '1px solid #1e2a3a' } }}
      >
        <DialogTitle sx={{ fontWeight: 900 }}>{cancelTarget?.event_type === 'game' || cancelTarget?.kind === 'matchup' ? 'Cancel Game?' : 'Cancel Event?'}</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1.5 }}>
            {cancelTarget?.event_type === 'game' || cancelTarget?.kind === 'matchup'
              ? <>{cancelTarget?.ownerTeamName} {cancelTarget && formatGameOpponentLine(cancelTarget)}</>
              : (cancelTarget?.title || 'This event')} stays on the schedule, clearly marked Cancelled.
            {cancelTarget?.kind === 'matchup' && ' Both Teams will see the updated status.'}
          </Typography>
          {cancelTarget?.series_id && <RecurringApplyToRadios value={cancelApplyTo} onChange={setCancelApplyTo} />}
          {cancelError && <Alert severity="error" sx={{ mt: 1 }}>{cancelError}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setCancelTarget(null)} disabled={cancelling} sx={{ textTransform: 'none', color: '#9eb8d0' }}>Back</Button>
          <Button onClick={handleCancelConfirm} disabled={cancelling} variant="contained" color="secondary" sx={{ textTransform: 'none', fontWeight: 700 }}>
            {cancelling ? <CircularProgress size={18} sx={{ color: '#050c16' }} /> : 'Cancel'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => { if (!deleting) setDeleteTarget(null); }}
        PaperProps={{ sx: { backgroundColor: 'rgba(10,20,34,0.98)', border: '1px solid #1e2a3a' } }}
      >
        <DialogTitle sx={{ fontWeight: 900 }}>{deleteTarget?.kind === 'matchup' ? 'Delete Shared Matchup?' : 'Delete Event?'}</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1.5 }}>
            {deleteTarget?.series_id ? (
              <>This removes <strong>{deleteTarget?.title || 'this event'}</strong> from the schedule. This cannot be undone.</>
            ) : (
              <>
                This permanently deletes{' '}
                <strong>
                  {deleteTarget?.event_type === 'game' || deleteTarget?.kind === 'matchup'
                    ? <>{deleteTarget?.ownerTeamName} {deleteTarget && formatGameOpponentLine(deleteTarget)}</>
                    : (deleteTarget?.title || 'this event')}
                </strong>. This cannot be undone.
              </>
            )}
            {deleteTarget?.kind === 'matchup' && ' It will also be removed from the other Team’s schedule and record.'}
          </Typography>
          {deleteTarget?.series_id && <RecurringApplyToRadios value={deleteApplyTo} onChange={setDeleteApplyTo} />}
          {deleteError && <Alert severity="error" sx={{ mt: 1 }}>{deleteError}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting} sx={{ textTransform: 'none', color: '#9eb8d0' }}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} disabled={deleting} variant="contained" color="error" sx={{ textTransform: 'none', fontWeight: 700 }}>
            {deleting ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );

  if (isTeamFocused) {
    return (
      <TeamHubLayout team={team} active="schedule">
        {divisionHref && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
            <Button component={Link} to={divisionHref} size="small" sx={{ textTransform: 'none', fontWeight: 600, color: '#6a8aa8' }}>
              View Full Division →
            </Button>
          </Box>
        )}
        {hub}
      </TeamHubLayout>
    );
  }

  // Phase C.5e.2 — never a hardcoded, Program-management-only link: a
  // Program Owner/Admin gets "← Seasons & Divisions"; a plain Head Coach
  // who arrived via their own Team's "View Full Division →" gets "← {Team
  // Name}"; anyone else (a direct URL visit with no Program authority and
  // no valid originating Team) gets the always-safe "← My Teams".
  const backNav = resolveDivisionBackNav({ canManage, programId, fromTeamId, perTeam });

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Button onClick={() => navigate(backNav.href)} sx={{ textTransform: 'none', color: '#6a8aa8', mb: 2, pl: 0 }}>
        {backNav.label}
      </Button>
      {hub}
    </Container>
  );
}
